import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { KeycloakService } from 'keycloak-angular';
import { Subscription } from 'rxjs';
import { Messages } from 'src/app/constants/messages';
import { NotificationMessageComponent } from 'src/app/dialogs/notification-message/notification-message.component';
import { ValidateSameValues } from 'src/app/model/local/details.orders';
import { DialogService } from 'src/app/services/dialog.service';
import {
  Actions,
  BooleanCons,
  EstatusTypeCard,
  MessageType,
  NumberCons,
  RolesString,
  StringConst,
  TooltipText,
} from '../../constants/const';
import {
  GeneralMessage,
  TypeDeleteProduct,
  TypeGeneralResponseOption,
  TypeIcon,
} from '../../model/device/message.model';
import { CallPlace, IProducts } from '../../model/http/myActivity.model';
import {
  DirectionsAndTaxs,
  RecipeSettingsObj,
  UpdateAddress,
  UpdateDataTax,
} from '../../model/http/profile.model';
import {
  AddressType,
  DefaultAddress,
  RecipeSettingsActions,
} from '../../model/local/general.model';
import { DeleteProduct } from '../../model/local/shopping-cart';
import { CommonService } from '../../services/common.service';
import { LocalStorageService } from '../../services/local-storage.service';
import { MyProfileService } from '../../services/my-profile.service';
import { ObservableService } from '../../services/observable.service';
import { ShoppingCartService } from '../../services/shopping-cart.service';
import { v4 as uuidv4 } from 'uuid';
import { DeleteItemsRequest } from 'src/app/model/local/missing-list-product.model';
import { DeleteItemsService } from 'src/app/services/delete-items.service';
import { DirectionsTypeCreate } from 'src/app/model/local/directions.type';

@Component({
  selector: 'app-delete-product',
  templateUrl: './delete-product.component.html',
  styleUrls: ['./delete-product.component.scss'],
})
export class DeleteProductComponent implements OnInit, OnDestroy {
  @Input() product = new IProducts();
  @Input() message: string = StringConst.empty;
  @Input() disableButton = false;
  @Output() wasDeleted = new EventEmitter<boolean>();
  @Input() callPlace = CallPlace.default;
  @Input() address = new DirectionsAndTaxs();
  @Input() listAddressOrTaxData: string[] = [];
  @Input() showHasText = BooleanCons.false;
  @Input() isPackage = BooleanCons.false;
  @Input() completeDeleteShoppingCart = false;
  @Input() attributeIndex = NumberCons.zero;
  @Input() typeDelete = TypeGeneralResponseOption.default;
  @Input() typeCart = EstatusTypeCard.withoutTypeCard;
  @Input() opacityText = BooleanCons.false;
  subscription = new Subscription();
  tooltip = StringConst.empty;
  isDefaultAddress = false;
  addressType = AddressType.none;
  cardCode = StringConst.empty;
  deleteId = uuidv4();
  constructor(
    private shoppingCartService: ShoppingCartService,
    private profileService: MyProfileService,
    private keycloak: KeycloakService,
    private localStorageService: LocalStorageService,
    private observableService: ObservableService,
    private commonService: CommonService,
    private dialogService: DialogService,
    private deleteItemsService: DeleteItemsService
  ) { }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  ngOnInit(): void {
    this.cardCode = this.commonService.getCarCodeFromDoctorOrAdvisor(
      this.keycloak.isUserInRole(RolesString.doctor)
    );
    this.setTooltipText();
    this.subscription.add(
      this.observableService.getResultGeneralNotification().subscribe((res) => {
        if (
          this.validateTypeDelete(
            res,
            TypeGeneralResponseOption.deleteProductFromDetailOrderListEdit
          )
        ) {
          this.deleteProductSelectedDetailOrder();
        } else if (
          this.validateTypeDelete(
            res,
            TypeGeneralResponseOption.deleteProductFromListShoppingCar
          )
        ) {
          this.deleteProductSelected();
        } else if (
          this.validateTypeDelete(
            res,
            TypeGeneralResponseOption.deleteDeliveryAddressFromList
          )
        ) {
          this.deleteDeliveryAddress();
        } else if (
          this.validateTypeDelete(res, TypeGeneralResponseOption.deleteTaxData)
        ) {
          this.deleteTaxAddress();
        } else if (
          this.validateTypeDelete(
            res,
            TypeGeneralResponseOption.deleteDeliveryAddressFromRecipeSettings
          )
        ) {
          this.deleteRecipeAddress();
        }
      })
    );
    this.isDefaultAddress = this.address.isDefault ?? false;
  }
  validateTypeDelete(
    res: GeneralMessage,
    typeDelete: TypeGeneralResponseOption
  ): boolean {
    return this.commonService.validateAndValue([
      res.typeOption === typeDelete,
      res.componentId === this.deleteId,
    ]);
  }

  onClick(): void {
    this.showConfirmationDialog();
  }

  showConfirmationDialog(): void {
    this.dialogService.openNotificationGeneric(NotificationMessageComponent, {
      typeMessage: MessageType.delete,
      textToShow: this.message,
      typeIcon: TypeIcon.question,
      isButtonCancel: true,
      itemCode: this.product.itemCode,
      address: this.address,
      componentId: this.deleteId,
      typeOption: this.typeDelete,
    });
  }

  deleteProductSelected(): void {
    if (this.completeDeleteShoppingCart) {
      this.deleteProduct();
    } else {
      this.deleteProductAttribute();
    }
  }
  deleteProduct(): void {
    const req = new DeleteItemsRequest();
    req.itemCode = this.product.productAttributes.map((attribute) => {
      return {
        itemCode: this.product.itemCode,
        container: attribute.container,
        label: attribute.originLabel,
      };
    });
    req.userId = this.localStorageService.getCardCodeGeneral();
    req.cartType = this.typeCart;
    this.deleteItemsService.deleteItems(req).subscribe(() => {
      this.wasDeleted.emit(true);
    });
  }
  deleteProductAttribute(): void {
    const deleteProduct = new DeleteProduct();
    deleteProduct.cartType = this.typeCart;
    deleteProduct.itemCode = this.product.itemCode;
    deleteProduct.userId = this.localStorageService.getCardCodeGeneral();
    deleteProduct.container =
      this.product.productAttributes[this.attributeIndex].container;
    deleteProduct.label =
      this.product.productAttributes[this.attributeIndex].originLabel;
    this.shoppingCartService
      .deleteProductService(deleteProduct)
      .subscribe((res) => {
        res.code !== 200
          ? this.launchControlledError(res.userError)
          : this.wasDeleted.emit(true);
      });
  }

  deleteProductSelectedDetailOrder(): void {
    this.wasDeleted.emit(false);
  }

  deleteDeliveryAddress(): void {
    this.addressType = AddressType.delivered;
    const addressToDelete: UpdateAddress = {
      doctorId: this.cardCode,
      addressId: this.address.shippingAddressNickName ?? StringConst.empty,
      zipCode: this.address.zipCode ?? StringConst.empty,
      state: this.address.state ?? StringConst.empty,
      city: this.address.city ?? StringConst.empty,
      neighborhood: this.address.suburb ?? StringConst.empty,
      street: this.address.stateAbr ?? StringConst.empty,
      number: this.address.streetNumber,
      phone: this.address.phoneNumber,
      etablishmentName: this.address.etablishmentName ?? StringConst.empty,
      betweenStreets: this.address.betweenStreets ?? StringConst.empty,
      references: this.address.references ?? StringConst.empty,
      stateComplete: StringConst.empty,
      action: Actions.delete,
      contact: StringConst.empty,
      addressType: DirectionsTypeCreate.default,
      patientEmail: this.address.patientEmail ?? StringConst.empty,
      responsibleId: NumberCons.zero,
      licenseNumber: StringConst.empty,
      doctorResponsibleName: StringConst.empty,
    };
    this.profileService
      .saveDirectionsDelivered(addressToDelete)
      .subscribe((res) => {
        res.code !== 200
          ? this.launchControlledError(res.userError)
          : this.showDeleteDeliveryAddressDialog();
      });
  }

  showDeleteDeliveryAddressDialog(): void {
    this.dialogService.openNotificationGeneric(NotificationMessageComponent, {
      textToShow: Messages.deliveryAddressDeleted,
      typeIcon: TypeIcon.success,
      typeMessage: MessageType.default,
    });
    this.setDefaultAddressService(CallPlace.deliveryAddresses);
  }

  getAddressDefaultObject(): DefaultAddress {
    const defaultAddress = new DefaultAddress();
    defaultAddress.doctorId = this.cardCode;
    defaultAddress.addressName = this.listAddressOrTaxData[1];
    defaultAddress.type = this.addressType;
    return defaultAddress;
  }

  setDefaultAddressService(callPlace: CallPlace): void {
    if (
      this.commonService.validateAndValue([
        this.isDefaultAddress,
        this.listAddressOrTaxData.length > 1,
      ])
    ) {
      const defaultAddress = this.getAddressDefaultObject();
      this.profileService
        .saveDefaultAddress(defaultAddress)
        .subscribe((res) => {
          res.code !== 200
            ? this.launchControlledError(res.userError)
            : this.observableService.setReloadData({
              callPlace,
              index: NumberCons.zero,
            });
        });
      return;
    }
    this.observableService.setReloadData({
      callPlace,
      index: NumberCons.zero,
    });
  }

  deleteTaxAddress(): void {
    this.addressType = AddressType.tax;
    const deleteTaxAddress: UpdateDataTax = new UpdateDataTax();
    deleteTaxAddress.doctorId = this.cardCode;
    deleteTaxAddress.action = Actions.delete;
    deleteTaxAddress.nickName = this.address.invoiceDataNickName;
    deleteTaxAddress.rfc = this.address.rfc;
    deleteTaxAddress.zipCode = this.address.zipCode;
    deleteTaxAddress.email = this.address.email;
    deleteTaxAddress.cfdi = this.address.cfdiDescription;
    deleteTaxAddress.cfdiDescription = this.address.cfdiDescription;
    deleteTaxAddress.cfdiValue = this.address.cfdiValue;
    deleteTaxAddress.stateAbr = this.address.stateAbr;
    this.profileService
      .saveTaxData(deleteTaxAddress)
      .subscribe((res) => {
        res.code !== 200
          ? this.launchControlledError(res.userError)
          : this.showDeleteTaxAdressDialog();
      });
  }

  showDeleteTaxAdressDialog(): void {
    this.setDefaultAddressService(CallPlace.taxData);
    this.dialogService.openNotificationGeneric(NotificationMessageComponent, {
      textToShow: Messages.taxAddressDeleted,
      typeIcon: TypeIcon.success,
      typeMessage: MessageType.default,
    });
  }

  setTooltipText(): void {
    const tooltipOptions: { [key: number]: string } = {
      3: this.commonService.calculateTernary(
        this.typeCart === EstatusTypeCard.savedCart, TooltipText.shoppingCarSaved, TooltipText.shoppingCar
      ),
      14: StringConst.empty,
      10: StringConst.empty,
      5: TooltipText.detailOrderEdit,
      20: StringConst.empty,
    };

    this.tooltip = tooltipOptions[this.callPlace];
  }

  deleteRecipeAddress(): void {
    const recipeObj = new RecipeSettingsObj();
    recipeObj.action = RecipeSettingsActions.delete;
    recipeObj.address = this.address;
    this.observableService.setRecipeSettingsAction(recipeObj);
  }

  launchControlledError(message?: string): void {
    const text = this.commonService.calculateTernary(
      message !== undefined,
      message,
      Messages.generic
    );
    this.dialogService.openNotificationGeneric(NotificationMessageComponent, {
      typeIcon: TypeIcon.error,
      textToShow: text,
      isButtonCancel: false,
      typeMessage: MessageType.none,
    });
  }
}
